import { useMemo, useState, type FormEvent } from 'react';
import { ChevronDown, ChevronUp, Plus } from 'lucide-react';
import type {
  InvoiceIngestApplyItem,
  InvoiceIngestParseItem,
} from '@stfors/shared-types';
import { ApiError } from '../../api/client';
import { useApplyInvoiceIngest } from '../../api/queries/invoice-ingest';
import { useClients } from '../../api/queries/clients';
import { useProjects } from '../../api/queries/projects';
import { ProjectOptions } from '../ProjectOptions';
import { useBankAccounts } from '../../api/queries/bank-accounts';
import { DateInput } from '../DateInput';
import { MoneyInput } from '../MoneyInput';
import { LookupSelect } from '../LookupSelect';
import { IconAction } from '../IconAction';
import './engagement-ingest.css';
import './invoice-ingest.css';
import { Toggle } from '../Toggle';

interface LineDraft {
  description: string;
  quantity: string;
  unit_amount: string;
}

interface InvoiceDraft {
  index: number;
  source_filename: string;
  include: boolean;
  parseError: string | null;
  client_id: number | null;
  project_id: number | null;
  invoice_number: string;
  issue_date: number | null;
  due_date: number | null;
  status: 'draft' | 'sent' | 'paid';
  tax_amount: string;
  notes: string;
  lines: LineDraft[];
  duplicate_invoice_number: boolean;
  bill_to_hint: string;
  // Payment detail — only used when status === 'paid'.
  pay_bank_account_id: number | null;
  pay_method: string;
  pay_reference: string;
  pay_date: number | null;
}

function money(n: number): string {
  return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function lineTotal(l: LineDraft): number {
  const q = Number(l.quantity);
  const u = Number(l.unit_amount);
  if (!Number.isFinite(q) || !Number.isFinite(u)) return 0;
  return Math.round(q * u * 100) / 100;
}

function buildDrafts(items: InvoiceIngestParseItem[]): InvoiceDraft[] {
  return items.map((item) => {
    const x = item.extracted;
    const suggested = item.suggested_client_matches[0]?.id ?? null;
    const lines: LineDraft[] =
      x?.line_items.map((li) => ({
        description: li.description,
        quantity: li.quantity,
        unit_amount: li.unit_amount,
      })) ?? [];
    if (lines.length === 0 && !item.error) {
      lines.push({ description: '', quantity: '1', unit_amount: '0.00' });
    }
    return {
      index: item.index,
      source_filename: item.source_filename,
      include: item.error == null && x != null,
      parseError: item.error,
      client_id: suggested,
      project_id: null,
      invoice_number: x?.invoice_number.value ?? '',
      issue_date: x?.issue_date.value ?? null,
      due_date: x?.due_date.value ?? null,
      status: 'draft' as const,
      tax_amount: x?.tax_amount.value ?? '0.00',
      notes: x?.notes.value ?? '',
      lines,
      duplicate_invoice_number: item.duplicate_invoice_number,
      bill_to_hint: x?.bill_to_name.value
        ? `${x.bill_to_name.value}${x.bill_to_email.value ? ` · ${x.bill_to_email.value}` : ''}`
        : '',
      pay_bank_account_id: null,
      pay_method: '',
      pay_reference: '',
      pay_date: null,
    };
  });
}

interface Props {
  items: InvoiceIngestParseItem[];
  pdfByIndex: Record<number, string>;
  sourceFiles: File[];
  onApplied: (createdCount: number) => void;
  onCancel: () => void;
  onBack: () => void;
}

export function InvoicePdfReview(props: Props): JSX.Element {
  const { items, pdfByIndex, onApplied, onCancel, onBack } = props;
  const apply = useApplyInvoiceIngest();
  const clientsQ = useClients();
  const clients = clientsQ.data?.clients ?? [];
  const bankAccountsQ = useBankAccounts(true);
  const bankAccounts = bankAccountsQ.data?.bank_accounts ?? [];

  const [drafts, setDrafts] = useState<InvoiceDraft[]>(() => buildDrafts(items));
  const [expanded, setExpanded] = useState<number | null>(
    () => drafts.find((d) => d.include)?.index ?? drafts[0]?.index ?? null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [resultSummary, setResultSummary] = useState<string | null>(null);

  const clientsForSelect = useMemo(
    () =>
      clients.map((c) => ({
        id: c.id,
        label: c.name ?? `Client #${c.id}`,
      })),
    [clients],
  );

  const update = (index: number, patch: Partial<InvoiceDraft>): void => {
    setDrafts((prev) => prev.map((d) => (d.index === index ? { ...d, ...patch } : d)));
  };

  const updateLine = (index: number, lineIdx: number, patch: Partial<LineDraft>): void => {
    setDrafts((prev) =>
      prev.map((d) => {
        if (d.index !== index) return d;
        const lines = d.lines.map((l, i) => (i === lineIdx ? { ...l, ...patch } : l));
        return { ...d, lines };
      }),
    );
  };

  const addLine = (index: number): void => {
    setDrafts((prev) =>
      prev.map((d) =>
        d.index === index
          ? { ...d, lines: [...d.lines, { description: '', quantity: '1', unit_amount: '0.00' }] }
          : d,
      ),
    );
  };

  const removeLine = (index: number, lineIdx: number): void => {
    setDrafts((prev) =>
      prev.map((d) => {
        if (d.index !== index) return d;
        if (d.lines.length <= 1) return d;
        return { ...d, lines: d.lines.filter((_, i) => i !== lineIdx) };
      }),
    );
  };

  const included = drafts.filter((d) => d.include && !d.parseError);
  const selectedTotal = included.reduce(
    (acc, d) => acc + d.lines.reduce((a, l) => a + lineTotal(l), 0) + Number(d.tax_amount || 0),
    0,
  );

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);
    setResultSummary(null);

    const payload: InvoiceIngestApplyItem[] = [];
    for (const d of included) {
      if (d.client_id == null) {
        setFormError(`Pick a client for ${d.source_filename} (${d.invoice_number || 'no number'}).`);
        setExpanded(d.index);
        return;
      }
      if (!d.invoice_number.trim()) {
        setFormError(`Invoice number is required for ${d.source_filename}.`);
        setExpanded(d.index);
        return;
      }
      if (d.issue_date == null || d.due_date == null) {
        setFormError(`Issue and due dates are required for ${d.invoice_number}.`);
        setExpanded(d.index);
        return;
      }
      if (d.due_date < d.issue_date) {
        setFormError(`Due date must be on or after issue date for ${d.invoice_number}.`);
        setExpanded(d.index);
        return;
      }
      for (const l of d.lines) {
        if (!l.description.trim()) {
          setFormError(`Every line needs a description (${d.invoice_number}).`);
          setExpanded(d.index);
          return;
        }
      }
      payload.push({
        index: d.index,
        client_id: d.client_id,
        project_id: d.project_id,
        invoice_number: d.invoice_number.trim(),
        issue_date: d.issue_date,
        due_date: d.due_date,
        status: d.status,
        tax_amount: d.tax_amount || '0',
        notes: d.notes.trim() || null,
        lines: d.lines.map((l) => ({
          description: l.description.trim(),
          quantity: l.quantity,
          unit_amount: l.unit_amount,
        })),
        pdf_base64: pdfByIndex[d.index] ?? null,
        original_filename: d.source_filename,
        payment: d.status === 'paid'
          ? {
              bank_account_id: d.pay_bank_account_id ?? undefined,
              method: d.pay_method || undefined,
              reference_number: d.pay_reference.trim() || undefined,
              payment_date: d.pay_date ?? undefined,
            }
          : undefined,
      });
    }

    if (payload.length === 0) {
      setFormError('Select at least one invoice to import.');
      return;
    }

    try {
      const res = await apply.mutateAsync({ items: payload });
      const ok = res.results.filter((r) => r.ok);
      const fail = res.results.filter((r) => !r.ok);
      if (fail.length === 0) {
        onApplied(ok.length);
        return;
      }
      const okIdx = new Set(ok.map((r) => r.index));
      setDrafts((prev) =>
        prev.map((d) => (okIdx.has(d.index) ? { ...d, include: false } : d)),
      );
      setResultSummary(
        `Created ${ok.length} invoice(s). ${fail.length} failed: ${fail.map((f) => f.error).join('; ')}`,
      );
    } catch (err) {
      setFormError(
        err instanceof ApiError ? err.message : 'Could not create invoices. Try again.',
      );
    }
  };

  const submitLabel = apply.isPending
    ? 'Creating…'
    : included.length === 1
      ? 'Add invoice'
      : `Add ${included.length} invoices`;

  return (
    <form className="eli-review inv-review" onSubmit={(e) => void onSubmit(e)}>
      <div className="eli-review-head">
        <div>
          <span className="eli-review-title">Confirm imported invoices</span>
          <span className="eli-review-file">
            {included.length} of {drafts.length} selected
            {included.length > 0 ? ` · ~${money(selectedTotal)}` : ''}
            {' · '}PDFs archived under Documents
          </span>
        </div>
        <div className="inv-review-head-actions">
          <button
            type="button"
            className="lgd-link"
            onClick={onBack}
            disabled={apply.isPending}
          >
            ← Choose different files
          </button>
          <IconAction action="cancel" onClick={onCancel} disabled={apply.isPending} label="Cancel import" />
          <button
            type="submit"
            className="btn-cyan-primary"
            disabled={apply.isPending || included.length === 0}
          >
            {submitLabel}
          </button>
        </div>
      </div>

      <p className="eli-review-hint">
        Review each invoice before saving. Uncheck any you want to skip — nothing is written until you confirm.
      </p>

      {(formError || resultSummary) && (
        <p className={formError ? 'inv-ingest-error' : 'inv-ingest-ok'} role="alert">
          {formError ?? resultSummary}
        </p>
      )}

      <ul className="inv-review-list">
        {drafts.map((d) => {
          const open = expanded === d.index;
          const sub = d.lines.reduce((a, l) => a + lineTotal(l), 0);
          return (
            <li
              key={d.index}
              className={`inv-review-card${d.include ? '' : ' is-skipped'}${d.parseError ? ' is-error' : ''}`}
            >
              <div className="inv-review-card-head">
                <Toggle
                  checked={d.include}
                  onChange={(v) => update(d.index, { include: v })}
                  label={d.invoice_number || d.source_filename}
                  disabled={!!d.parseError}
                  className="inv-review-check"
                />
                <span className="inv-review-card-sub">
                  {d.source_filename}
                  {d.bill_to_hint ? ` · ${d.bill_to_hint}` : ''}
                  {!d.parseError ? ` · ${money(sub)}` : ''}
                </span>
                {d.duplicate_invoice_number && (
                  <span className="eli-flag" title="This invoice number already exists">
                    duplicate #
                  </span>
                )}
                {d.parseError && (
                  <span className="inv-flag inv-flag-err">{d.parseError}</span>
                )}
                <button
                  type="button"
                  className="icon-btn inv-review-toggle"
                  title={open ? 'Collapse' : 'Edit'}
                  aria-label={open ? 'Collapse' : 'Edit'}
                  aria-expanded={open}
                  onClick={() => setExpanded(open ? null : d.index)}
                  disabled={!!d.parseError}
                >
                  {open
                    ? <ChevronUp size={18} strokeWidth={1.75} aria-hidden />
                    : <ChevronDown size={18} strokeWidth={1.75} aria-hidden />}
                </button>
              </div>

              {open && !d.parseError && (
                <div className="inv-review-body">
                  <div className="eli-grid">
                    <label className="lgd-field">
                      <span>Client</span>
                      <select
                        className="form-input"
                        value={d.client_id ?? ''}
                        onChange={(e) =>
                          update(d.index, {
                            client_id: e.target.value ? Number(e.target.value) : null,
                            project_id: null,
                          })
                        }
                        required={d.include}
                      >
                        <option value="">Select client…</option>
                        {clientsForSelect.map((c) => (
                          <option key={c.id} value={c.id}>{c.label}</option>
                        ))}
                      </select>
                    </label>
                    <ProjectPicker
                      clientId={d.client_id}
                      value={d.project_id}
                      onChange={(project_id) => update(d.index, { project_id })}
                    />
                    <label className="lgd-field">
                      <span>Invoice #</span>
                      <input
                        className="form-input"
                        type="text"
                        value={d.invoice_number}
                        onChange={(e) => update(d.index, { invoice_number: e.target.value })}
                        required={d.include}
                      />
                    </label>
                    <label className="lgd-field">
                      <span>Status</span>
                      <select
                        className="form-input"
                        value={d.status}
                        onChange={(e) =>
                          update(d.index, { status: e.target.value as InvoiceDraft['status'] })
                        }
                      >
                        <option value="draft">Draft</option>
                        <option value="sent">Sent</option>
                        <option value="paid">Paid</option>
                      </select>
                    </label>
                    <label className="lgd-field">
                      <span>Issue date</span>
                      <DateInput
                        value={d.issue_date}
                        onChange={(v) => update(d.index, { issue_date: v })}
                      />
                    </label>
                    <label className="lgd-field">
                      <span>Due date</span>
                      <DateInput
                        value={d.due_date}
                        onChange={(v) => update(d.index, { due_date: v })}
                      />
                    </label>
                    <label className="lgd-field">
                      <span>Tax</span>
                      <MoneyInput
                        value={d.tax_amount}
                        onChange={(v) => update(d.index, { tax_amount: v })}
                      />
                    </label>
                    {d.status === 'paid' && (
                      <>
                        <label className="lgd-field">
                          <span>Deposited to</span>
                          <select
                            className="form-input"
                            value={d.pay_bank_account_id == null ? '' : String(d.pay_bank_account_id)}
                            onChange={(e) =>
                              update(d.index, {
                                pay_bank_account_id: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          >
                            <option value="">— (none) —</option>
                            {bankAccounts.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.label}{a.account_last4 ? ` ••••${a.account_last4}` : ''}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label className="lgd-field">
                          <span>Method</span>
                          <LookupSelect
                            category="payment_method"
                            value={d.pay_method}
                            onChange={(v) => update(d.index, { pay_method: v })}
                            allowEmpty
                          />
                        </label>
                        <label className="lgd-field">
                          <span>Check # / ref</span>
                          <input
                            className="form-input"
                            type="text"
                            value={d.pay_reference}
                            onChange={(e) => update(d.index, { pay_reference: e.target.value })}
                            maxLength={200}
                          />
                        </label>
                        <label className="lgd-field">
                          <span>Payment date</span>
                          <DateInput
                            value={d.pay_date ?? d.issue_date}
                            onChange={(v) => update(d.index, { pay_date: v })}
                          />
                        </label>
                      </>
                    )}
                    <label className="lgd-field eli-col-2">
                      <span>Notes</span>
                      <input
                        className="form-input"
                        type="text"
                        value={d.notes}
                        onChange={(e) => update(d.index, { notes: e.target.value })}
                      />
                    </label>
                  </div>

                  <div className="inv-review-lines">
                    <div className="inv-review-lines-head">
                      <span>Line items</span>
                      <button
                        type="button"
                        className="icon-btn icon-btn-cyan"
                        title="Add line"
                        aria-label="Add line"
                        onClick={() => addLine(d.index)}
                      >
                        <Plus size={18} strokeWidth={1.75} aria-hidden />
                      </button>
                    </div>
                    <table className="inv-lines-table">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit</th>
                          <th>Amount</th>
                          <th />
                        </tr>
                      </thead>
                      <tbody>
                        {d.lines.map((l, li) => (
                          <tr key={li}>
                            <td>
                              <input
                                className="form-input"
                                type="text"
                                value={l.description}
                                onChange={(e) =>
                                  updateLine(d.index, li, { description: e.target.value })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="form-input"
                                type="text"
                                inputMode="decimal"
                                value={l.quantity}
                                onChange={(e) =>
                                  updateLine(d.index, li, { quantity: e.target.value })
                                }
                              />
                            </td>
                            <td>
                              <MoneyInput
                                value={l.unit_amount}
                                onChange={(v) => updateLine(d.index, li, { unit_amount: v })}
                              />
                            </td>
                            <td className="inv-line-amt">{money(lineTotal(l))}</td>
                            <td>
                              <IconAction
                                action="delete"
                                label="Remove line"
                                disabled={d.lines.length <= 1}
                                onClick={() => removeLine(d.index, li)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan={3}>Subtotal + tax</td>
                          <td className="inv-line-amt">
                            {money(sub + Number(d.tax_amount || 0))}
                          </td>
                          <td />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="eli-actions inv-review-foot">
        <button
          type="button"
          className="lgd-link"
          onClick={onBack}
          disabled={apply.isPending}
        >
          ← Choose different files
        </button>
        <button
          type="submit"
          className="btn-cyan-primary"
          disabled={apply.isPending || included.length === 0}
        >
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function ProjectPicker({
  clientId,
  value,
  onChange,
}: {
  clientId: number | null;
  value: number | null;
  onChange: (id: number | null) => void;
}): JSX.Element {
  const projectsQ = useProjects(clientId != null ? { client_id: clientId } : {});
  const projects = projectsQ.data?.projects ?? [];

  return (
    <label className="lgd-field">
      <span>Project (optional)</span>
      <select
        className="form-input"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)}
        disabled={clientId == null}
      >
        <option value="">None</option>
        <ProjectOptions projects={projects} />
      </select>
    </label>
  );
}
